import React, { useState, useEffect } from 'react';
import { Upload, Search, Filter } from 'lucide-react';
import api from '../../utils/api';
import MainLayout from '../../components/layout/MainLayout';
import MediaFileCard from './components/MediaFileCard';
import MediaUploadModal from './MediaUploadModal';

const MediaLibraryPage = () => {
  const [files, setFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeType, setActiveType] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [activeCategory, activeType, searchQuery, showArchived]);

  const fetchCategories = async () => {
    try {
      const res = await api('/media-library/categories');
      if (res.ok) {
        const data = await res.json();
        setCategories(['All', ...data]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (activeCategory !== 'All') queryParams.append('category', activeCategory);
      if (activeType !== 'All') queryParams.append('type', activeType);
      if (searchQuery) queryParams.append('search', searchQuery);
      if (showArchived) queryParams.append('archived', 'true');

      const res = await api(`/media-library?${queryParams.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setFiles(data);
      }
    } catch (err) {
      console.error('Failed to fetch media files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (newFile) => {
    fetchFiles();
    fetchCategories();
  };

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>Universal Media Library</h1>
            <p style={{ color: 'var(--color-text-secondary)' }}>Upload files here to use them across WhatsApp API and WhatsApp Web campaigns.</p>
          </div>
          <button 
            onClick={() => setIsUploadModalOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: 'var(--color-brand-primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
          >
            <Upload size={18} /> Upload Media
          </button>
        </div>

        {/* Status Toggle */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <button 
            onClick={() => setShowArchived(false)}
            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', background: !showArchived ? 'white' : 'transparent', fontWeight: !showArchived ? 'bold' : 'normal', cursor: 'pointer' }}
          >
            Active Files
          </button>
          <button 
            onClick={() => setShowArchived(true)}
            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', background: showArchived ? 'white' : 'transparent', fontWeight: showArchived ? 'bold' : 'normal', cursor: 'pointer' }}
          >
            Archived Files
          </button>
        </div>

        {/* Filters */}
        <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid var(--color-border-primary)', marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          
          <div style={{ position: 'relative', flex: '1 1 300px' }}>
            <Search size={18} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input 
              type="text" 
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid var(--color-border-primary)' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', borderRight: '1px solid var(--color-border-primary)', paddingRight: '16px' }}>
            <Filter size={18} color="var(--color-text-secondary)" />
            <select 
              value={activeCategory} 
              onChange={(e) => setActiveCategory(e.target.value)}
              style={{ padding: '10px', borderRadius: '8px', border: '1px solid var(--color-border-primary)', background: 'var(--color-background-primary)' }}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
              {categories.length === 0 && <option value="All">All Categories</option>}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {['All', 'Image', 'Video', 'Document', 'Audio'].map(type => (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                style={{ 
                  padding: '8px 16px', 
                  borderRadius: '20px', 
                  border: '1px solid',
                  borderColor: activeType === type ? 'var(--color-brand-primary)' : 'var(--color-border-primary)',
                  background: activeType === type ? 'var(--color-brand-primary)' : 'white',
                  color: activeType === type ? 'white' : 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                {type}
              </button>
            ))}
          </div>

        </div>

        {/* File Grid */}
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Loading files...</div>
        ) : files.length === 0 ? (
          <div style={{ background: 'white', padding: '60px 20px', textAlign: 'center', borderRadius: '12px', border: '1px dashed var(--color-border-primary)' }}>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '16px' }}>No media files found matching your criteria.</p>
            <button 
              onClick={() => setIsUploadModalOpen(true)}
              style={{ padding: '8px 16px', background: 'white', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)', borderRadius: '6px', cursor: 'pointer' }}
            >
              Upload First File
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
            {files.map(file => (
              <MediaFileCard key={file._id} file={file} onRefresh={fetchFiles} />
            ))}
          </div>
        )}
      </div>

      <MediaUploadModal 
        isOpen={isUploadModalOpen} 
        onClose={() => setIsUploadModalOpen(false)} 
        onUploadSuccess={handleUploadSuccess} 
      />
    </MainLayout>
  );
};

export default MediaLibraryPage;
